import { useState, useEffect, useCallback } from "react";
import { privacyContent } from "../data/privacyContent";
import { Printer, Globe, ChevronUp, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import CookieBanner from "../components/CookieBanner";
import GdprRequestForm from "../components/GdprRequestForm";

export default function PrivacyPage() {
  const [lang, setLang] = useState("it");
  const [activeSection, setActiveSection] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);

  const content = privacyContent[lang];

  const handleScroll = useCallback(() => {
    setShowBackToTop(window.scrollY > 400);

    const sections = content.sections.map((s) => ({
      id: s.id,
      el: document.getElementById(s.id),
    }));

    let current = "";
    for (const section of sections) {
      if (section.el) {
        const rect = section.el.getBoundingClientRect();
        if (rect.top <= 140) {
          current = section.id;
        }
      }
    }
    setActiveSection(current);
  }, [content.sections]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]" data-testid="privacy-page">
      {/* Header */}
      <header
        className="relative overflow-hidden bg-[#0A0A0A] print:bg-white print:text-black"
        data-testid="privacy-header"
      >
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/3137073/pexels-photo-3137073.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="flex items-center justify-between mb-10 print:hidden">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#002FA7]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                {content.company}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="flex items-center bg-white/10 rounded-full p-0.5"
                data-testid="language-toggle"
              >
                <button
                  data-testid="language-toggle-it"
                  onClick={() => setLang("it")}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                    lang === "it"
                      ? "bg-[#002FA7] text-white"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  IT
                </button>
                <button
                  data-testid="language-toggle-en"
                  onClick={() => setLang("en")}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                    lang === "en"
                      ? "bg-[#002FA7] text-white"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  EN
                </button>
              </div>
              <Button
                data-testid="print-policy-button"
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="bg-transparent border-white/20 text-white hover:bg-white hover:text-[#0A0A0A] transition-all duration-200 rounded-full"
              >
                <Printer className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">
                  {lang === "it" ? "Stampa" : "Print"}
                </span>
              </Button>
            </div>
          </div>

          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-[#002FA7]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7]">
                GDPR
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-none text-white print:text-black"
              data-testid="privacy-title"
            >
              {content.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-400 print:text-slate-600 leading-relaxed">
              {content.subtitle}
            </p>
            <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              {content.lastUpdated}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12 lg:py-20">
        <div className="grid grid-cols-12 gap-8 lg:gap-16">
          {/* Table of Contents - Sticky Sidebar */}
          <aside className="col-span-12 md:col-span-4 lg:col-span-3 print:hidden">
            <div className="sticky top-24">
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-6">
                {lang === "it" ? "Indice" : "Table of Contents"}
              </h2>
              <ScrollArea className="h-[calc(100vh-200px)]">
                <nav className="space-y-1">
                  {content.sections.map((section) => (
                    <button
                      key={section.id}
                      data-testid="toc-link"
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-4 py-2.5 text-sm rounded-lg transition-all duration-200 block ${
                        activeSection === section.id
                          ? "bg-[#002FA7]/10 text-[#002FA7] font-semibold border-l-2 border-[#002FA7]"
                          : "text-slate-600 hover:bg-slate-100 hover:text-[#111111] border-l-2 border-transparent"
                      }`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </ScrollArea>
            </div>
          </aside>

          {/* Content */}
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            <div className="max-w-prose">
              {content.sections.map((section, idx) => (
                <section
                  key={section.id}
                  id={section.id}
                  data-testid="privacy-content-section"
                  className="scroll-mt-28"
                >
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-[#111111] mb-6">
                    {section.title}
                  </h2>
                  <div className="space-y-4">
                    {section.content.map((paragraph, pIdx) => (
                      <p
                        key={`${section.id}-p-${pIdx}`}
                        className={`text-base font-normal leading-relaxed ${
                          pIdx === 0
                            ? "text-slate-800"
                            : "text-slate-600 pl-4 border-l-2 border-slate-200"
                        }`}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  {idx < content.sections.length - 1 && (
                    <Separator className="my-10 bg-slate-200" />
                  )}
                </section>
              ))}
            </div>

            {/* Footer info */}
            <div className="mt-16 p-8 bg-white rounded-2xl border border-slate-200">
              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-[#002FA7] shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-semibold text-[#111111] mb-2">
                    {lang === "it" ? "Contatti Privacy" : "Privacy Contacts"}
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed">
                    {lang === "it"
                      ? "Per qualsiasi domanda riguardante la presente informativa o per esercitare i Suoi diritti, non esiti a contattarci."
                      : "For any questions regarding this policy or to exercise your rights, please do not hesitate to contact us."}
                  </p>
                  <a
                    href="mailto:info@sisaline.it"
                    data-testid="privacy-contact-email"
                    className="inline-flex items-center mt-4 text-[#002FA7] font-semibold hover:underline transition-all"
                  >
                    info@sisaline.it
                  </a>
                </div>
              </div>
            </div>

            {/* GDPR Request Form */}
            <div className="mt-8 print:hidden">
              <GdprRequestForm lang={lang} />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] py-8 print:hidden" data-testid="privacy-footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
            <p className="text-sm text-slate-500">
              &copy; {new Date().getFullYear()} {content.company}.{" "}
              {lang === "it"
                ? "Tutti i diritti riservati."
                : "All rights reserved."}
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Shield className="w-4 h-4" />
              <span>GDPR Compliant</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 border-t border-white/10 pt-4">
            <p className="text-xs text-slate-600" data-testid="footer-address">
              Via Monferrato, 20, 20098 San Giuliano Milanese (MI), Italia
            </p>
            <p className="text-xs text-slate-600" data-testid="footer-vat">
              P.IVA: 14184310960
            </p>
          </div>
        </div>
      </footer>

      {/* Back to top button */}
      {showBackToTop && (
        <button
          data-testid="back-to-top-button"
          onClick={scrollToTop}
          className="fixed bottom-20 right-8 w-12 h-12 bg-[#002FA7] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#001f75] transition-all duration-200 print:hidden z-[9999]"
          aria-label="Back to top"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}

      {/* Cookie Consent Banner */}
      <CookieBanner lang={lang} />
    </div>
  );
}
